import { useNavigate, useSearchParams } from '@solidjs/router'
import {
    AdminUserCount,
    api_admin_companies_get,
    api_admin_users_admin_perms_patch,
    api_admin_users_count_get,
    api_admin_users_del,
    api_admin_users_list,
    api_admin_users_patch,
    api_admin_users_search_list,
    User,
} from 'abi'
import {
    addAlert,
    CompBool,
    Counter,
    LoadingDots,
    LoadingElem,
    Pagination,
    SearchInp,
    setPopup,
} from 'components'
import { AddBtn } from 'components/addBtn'
import {
    BanIcon,
    CheckFillIcon,
    CloseIcon,
    DeleteIcon,
    ShieldCheckIcon,
    ShieldDisableIcon,
    UserIcon,
} from 'icons'
import {
    Component,
    createEffect,
    createSignal,
    For,
    on,
    onMount,
    Show,
} from 'solid-js'
import { createStore, produce, unwrap } from 'solid-js/store'

import { Perms, PERMS_SIZE } from 'pages/shared'
import { userRole } from 'store/self'
import { debounce } from 'utils/tools'
import { outletRef } from '..'
import { AddUserCmp } from './add'
import { isAdmin } from './corp/shared'
import './style/users.scss'
import { PROVINCE_FA } from '../profile'

type STATE = {
    count: AdminUserCount

    page: number
    max_page: number

    onlyAdmin: boolean | null

    add: {
        type: 'edit' | 'add'
        show: boolean
        user: User | null
    }

    filters: {
        admin: boolean
        users: boolean
    }

    query: string

    loadingC: boolean
    loadingU: boolean

    users: User[]

    error: boolean
}

const Users: Component = () => {
    let nav = useNavigate()

    const [state, setState] = createStore<STATE>({
        users: [],
        page: 0,

        max_page: -1,

        add: {
            show: false,
            type: 'add',
            user: null,
        },

        filters: {
            admin: false,
            users: false,
        },

        query: '',
        onlyAdmin: null,

        loadingU: true,
        loadingC: true,

        count: {
            total: -1,
            admins: -1,
            agents: -1,
            managers: -1,
        },
        error: false,
    })

    const [search_params, setSearchParams] = useSearchParams()

    createEffect(
        on(
            () => [state.filters, search_params.page] as const,
            ([_, page]) => {
                if (!isAdmin()) return

                if (state.query) {
                    return debouncedSetSearch(state.query)
                }

                setSearchParams({ page: parseInt(page || '0') })

                if (!page) return

                defaultSearch(parseInt(page))
            }
        )
    )

    onMount(() => {
        if (!isAdmin()) return nav('/panel/403', { replace: true })

        countSearch()
    })

    const debouncedSetSearch = debounce((query: string) => {
        search(query)
    }, 200)

    const search = async (query: string) => {
        setState({ loadingU: true, error: false })

        let page = unwrap(state.page)

        let res = await api_admin_users_search_list({
            company: null,
            page,
            username: `%${query}%`,
        })

        setState({ loadingU: false })

        if (!res.ok()) return setState({ error: true })

        if (state.query !== query) return

        setState(
            produce(s => {
                s.users = res.body
                s.count.total = res.body.length
            })
        )
    }

    const defaultSearch = async (page: number) => {
        setState({ loadingU: true })

        let is_admin = null

        if (state.filters.admin) {
            is_admin = true
        }
        if (state.filters.users) {
            is_admin = false
        }

        let ures = await api_admin_users_list({
            page,

            // TODO: DESIGN FOR ORDERS
            order: 'desc',
            is_admin,
        })

        if (ures.ok()) {
            setState({ page, users: ures.body })
        }

        setState({ loadingU: false })
    }

    const countSearch = async () => {
        setState({ loadingC: true })

        let res = await api_admin_users_count_get()

        if (res.ok()) {
            setState({
                count: res.body,
                max_page: Math.max(0, Math.floor(res.body.total / 32)),
            })
        }

        setState({ loadingC: false })
    }

    const removeUser = (idx: number) => {
        setState(
            produce(s => {
                let id = s.users.findIndex(f => f.id == idx)
                if (id == -1) return

                s.users.splice(id, 1)
                s.count.total -= 1
            })
        )
    }
    const toggleUserBan = (idx: number, ban: boolean) => {
        setState(
            produce(s => {
                let id = s.users.findIndex(f => f.id == idx)
                if (id == -1) return
                s.users[id]!.banned = ban
            })
        )
    }
    const toggleUserAdmin = (idx: number, perms: number[]) => {
        setState(
            produce(s => {
                let id = s.users.findIndex(f => f.id == idx)
                if (id == -1) return
                s.users[id]!.admin_perms = perms
            })
        )
    }

    return (
        <div class='users-section section'>
            <div class='users-wrapper'>
                <header class='head section_title'>
                    <UserIcon />
                    <span>کاربران</span>
                    <UserIcon />
                </header>

                <div class='totals-wrapper'>
                    <div
                        class='total-wrapper section_title'
                        classList={{ active: !state.loadingC }}
                    >
                        <Counter end={state.count.total} />
                        <span class='title_small'>کاربر</span>
                    </div>

                    <div
                        class='total-wrapper admins section_title'
                        classList={{ active: !state.loadingC }}
                    >
                        <Counter end={state.count.admins} />
                        <span class='title_small'>ادمین</span>
                    </div>
                </div>

                <AddBtn
                    onClick={() =>
                        setState('add', { show: true, user: null, type: 'add' })
                    }
                    class='title_smaller'
                    title='اضافه کردن'
                />

                <form
                    class='filters-container'
                    onsubmit={e => {
                        e.preventDefault()
                        search(state.query)
                    }}
                >
                    <SearchInp
                        value={state.query}
                        active={state.query.length !== 0}
                        oninput={e =>
                            setState({ query: e.currentTarget.value.trim() })
                        }
                        onClear={() => setState({ query: '' })}
                        id='users-search-inp'
                    />

                    <div
                        class='filters title_smaller'
                        classList={{ active: !state.query }}
                    >
                        <div class='filter'>
                            <CompBool
                                default={state.filters.admin}
                                onChange={v => {
                                    setState(
                                        produce(s => {
                                            s.filters = {
                                                users: false,
                                                admin: v,
                                            }
                                        })
                                    )
                                }}
                            />
                            فقط ادمین
                        </div>
                        <div class='filter'>
                            <CompBool
                                default={state.filters.users}
                                onChange={v => {
                                    setState(
                                        produce(s => {
                                            s.filters = {
                                                admin: false,
                                                users: v,
                                            }
                                        })
                                    )
                                }}
                            />
                            فقط کاربر
                        </div>
                    </div>
                </form>

                <div
                    class='table-container'
                    classList={{ min: state.users?.length == 0 }}
                >
                    <table>
                        <thead>
                            <tr class='title_small'>
                                <th>
                                    <span>نام</span>
                                </th>
                                <th>
                                    <span>نام کاربری</span>
                                </th>
                                <th>
                                    <span>استان</span>
                                </th>
                                <th>
                                    <span>استخدام شرکت</span>
                                </th>
                                <th>
                                    <span>نوع دسترسی</span>
                                </th>
                                <th class='date'>
                                    <span>تاریخ ثبت</span>
                                </th>
                                <th>
                                    <span>تغییر</span>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <Show
                                when={!state.loadingU}
                                fallback={
                                    <tr class='table-loading title_hero'>
                                        <td colspan='10'>
                                            <LoadingDots />
                                            <div>در حال بارگذاری...</div>
                                        </td>
                                    </tr>
                                }
                            >
                                <Show
                                    when={state.users.length > 0}
                                    fallback={
                                        <tr class='table-empty title_hero'>
                                            <td colspan='10'>
                                                <div class='icon'>
                                                    <CloseIcon />
                                                </div>
                                                <div>
                                                    اطلاعاتی برای نمایش وجود
                                                    ندارد!
                                                </div>
                                            </td>
                                        </tr>
                                    }
                                >
                                    <For each={state.users}>
                                        {u => (
                                            <TableRow
                                                user={u}
                                                page={state.page}
                                                onEdit={() =>
                                                    setState('add', {
                                                        type: 'edit',
                                                        user: { ...u },
                                                        show: true,
                                                    })
                                                }
                                                onBan={ban => {
                                                    toggleUserBan(u.id, ban)
                                                }}
                                                onDel={() => removeUser(u.id)}
                                                onAdmin={admin => {
                                                    toggleUserAdmin(u.id, admin)
                                                }}
                                            />
                                        )}
                                    </For>
                                </Show>
                            </Show>
                        </tbody>
                    </table>
                </div>

                <Show when={state.max_page >= 1}>
                    <div class='pagination-container'>
                        <Pagination
                            max_page={state.max_page}
                            current={state.page || 0}
                            onChange={page => {
                                setSearchParams({ page })
                                outletRef?.scrollTo({
                                    top: 0,
                                    behavior: 'smooth',
                                })
                            }}
                        />
                    </div>
                </Show>
            </div>

            <AddUserCmp
                onClose={() =>
                    setState('add', { type: 'add', user: null, show: false })
                }
                add={state.add}
                onAdd={c => {
                    setState(
                        produce(s => {
                            s.users.unshift(c)
                            s.count.total += 1
                        })
                    )
                }}
                onEdit={user => {
                    setState(
                        produce(s => {
                            let id = s.users.findIndex(c => c.id == user.id)

                            if (id != -1) {
                                s.users[id] = user
                            }
                        })
                    )
                }}
            />
        </div>
    )
}

type TableRowProps = {
    user: User
    page: number

    onDel(): void
    onEdit(): void
    onBan(ban: boolean): void
    onAdmin(admin: number[]): void
}
export const TableRow: Component<TableRowProps> = P => {
    const [loading, setLoading] = createSignal(false)

    const [corp, setCorp] = createStore({
        name: '',
        loading: true,
    })

    onMount(async () => {
        if (!P.user.staff_at) return

        let id = P.user.staff_at
        let res = await api_admin_companies_get({ id })

        if (!res.ok()) return

        setCorp({ loading: false, name: res.body.name })
    })
    // const nav = useNavigate()

    const hasPerms = () => {
        return new Perms(P.user.admin_perms || []).any()
    }

    const deleteUser = async () => {
        setLoading(true)

        let res = await api_admin_users_del(
            {
                id: P.user.id,
            },
            {
                show_alert: true,
            }
        )

        setLoading(false)

        if (!res.ok()) return

        let name = P.user.name || 'بدون نام'

        addAlert({
            type: 'success',
            timeout: 3,
            subject: 'موفق!',
            content: `کاربر ${name} با موفقیت حذف شد.`,
        })
        P.onDel()
    }
    const UnbanBtnCmp = () => {
        const unbanUser = async () => {
            setLoading(true)

            let res = await api_admin_users_patch(
                {
                    id: P.user.id,
                },
                {
                    name: P.user.name,
                    username: P.user.username,
                    password: null,
                    banned: false,
                    staff_at: P.user.staff_at,
                    staff_role: P.user.staff_role,
                    province: P.user.province,
                },
                {
                    show_alert: true,
                }
            )

            setLoading(false)

            if (!res.ok()) return

            let name = P.user.name

            addAlert({
                type: 'success',
                timeout: 3,
                subject: 'موفق!',
                content: `کاربر ${name} با موفقیت آنبن شد.`,
            })

            P.onBan(false)
        }
        return (
            <button
                class='green icon'
                onclick={e => {
                    e.stopPropagation()
                    setPopup({
                        show: true,
                        type: 'success',
                        timer: 2,
                        Icon: CheckFillIcon,
                        title: 'کاربر را آنبن میکنید؟',
                        content:
                            'بعد از آنبن تمام دسترسی های کاربر به سایت بر میگردد.',
                        onSubmit() {
                            unbanUser()
                        },
                    })
                }}
            >
                <CheckFillIcon />
            </button>
        )
    }
    const BanBtnCmp = () => {
        const banUser = async () => {
            setLoading(true)

            let res = await api_admin_users_patch(
                {
                    id: P.user.id,
                },
                {
                    name: P.user.name,
                    username: P.user.username,
                    password: null,
                    banned: true,
                    staff_at: P.user.staff_at,
                    staff_role: P.user.staff_role,
                    province: P.user.province,
                },
                {
                    show_alert: true,
                }
            )

            setLoading(false)

            if (!res.ok()) return

            let name = P.user.name

            addAlert({
                type: 'success',
                timeout: 3,
                subject: 'موفق!',
                content: `کاربر ${name} با موفقیت بن شد.`,
            })
            P.onBan(true)
        }
        return (
            <button
                class='red icon'
                onclick={e => {
                    e.stopPropagation()
                    setPopup({
                        show: true,
                        type: 'error',
                        timer: 3,
                        Icon: BanIcon,
                        title: 'کاربر را بن میکنید؟',
                        content:
                            'بعد از بن کاربر هیچ دسترسی ای به سایت نخواهد داشت!',
                        onSubmit() {
                            banUser()
                        },
                    })
                }}
            >
                <BanIcon />
            </button>
        )
    }

    const ALL_NON_MASTER_PERMS = [
        Perms.V_USER,
        Perms.A_USER,
        Perms.C_USER,
        Perms.D_USER,

        Perms.V_COMPANY,
        Perms.A_COMPANY,
        Perms.C_COMPANY,
        Perms.D_COMPANY,

        Perms.V_HELMET,
        Perms.A_HELMET,
        Perms.C_HELMET,
        Perms.D_HELMET,

        Perms.V_HELMET_CARGO,
        Perms.A_HELMET_CARGO,
        Perms.C_HELMET_CARGO,
        Perms.D_HELMET_CARGO,

        Perms.V_COOPERATION,
        Perms.A_COOPERATION,
        Perms.C_COOPERATION,
        Perms.D_COOPERATION,
    ]
    const toggleAdmin = async () => {
        setLoading(true)

        let perms: number[] = new Array(PERMS_SIZE).fill(0)

        if (!hasPerms()) {
            // start with zeroed bytes
            const pInst = new Perms(perms)

            // enable each perm one-by-one
            for (const p of ALL_NON_MASTER_PERMS) {
                pInst.set(p, true)
            }

            // get the resulting byte array
            perms = pInst.perms
        }

        let res = await api_admin_users_admin_perms_patch(
            {
                id: P.user.id,
            },
            {
                admin_perms: perms,
            },
            {
                show_alert: true,
            }
        )
        setLoading(false)

        if (!res.ok()) return

        addAlert({
            type: 'success',
            timeout: 3,
            subject: 'موفق!',
            content: 'دسترسی کاربر با موفقیت عوض شد',
        })

        P.onAdmin(res.body.admin_perms || [])
    }

    return (
        <tr class='title_smaller' onclick={() => P.onEdit()}>
            <Show
                when={!loading()}
                fallback={
                    <td class='loading-tr' colspan={6} aria-hidden='true'>
                        <LoadingElem />
                    </td>
                }
            >
                <td>
                    <div class='name' title={P.user.banned ? 'بن شده' : ''}>
                        <div
                            class='ban-icon icon'
                            classList={{ active: P.user.banned }}
                        >
                            <BanIcon />
                        </div>
                        {P.user.name || 'N / A'}
                    </div>
                </td>
            </Show>
            <td>{P.user.username || 'N / A'}</td>
            <td>{PROVINCE_FA[P.user.province]}</td>
            <td class='corp'>
                <Show
                    when={P.user.staff_at}
                    fallback={
                        <div class='no-corp'>
                            <CloseIcon />
                            بدون شرکت
                        </div>
                    }
                >
                    <Show when={!corp.loading} fallback={<LoadingDots />}>
                        {corp.name}
                    </Show>
                </Show>
            </td>
            <td>{userRole(P.user)}</td>

            <td class='date'>
                {new Date(P.user.created_at * 1e3).toLocaleDateString('fa-IR')}
            </td>
            <td>
                <div class='ctas'>
                    <Show when={!P.user.banned} fallback={<UnbanBtnCmp />}>
                        <BanBtnCmp />
                    </Show>

                    <button
                        class='yellow icon'
                        onclick={e => {
                            e.stopPropagation()
                            setPopup({
                                show: true,
                                type: 'error',
                                timer: 2,
                                Icon: DeleteIcon,
                                title: 'کاربر را حذف میکنید؟',
                                content:
                                    'بعد از حذف کاربر نیاز به ثبت مجدد آن است.',
                                onSubmit() {
                                    deleteUser()
                                },
                            })
                        }}
                    >
                        <DeleteIcon />
                    </button>
                    <Show
                        when={
                            !new Perms(P.user.admin_perms || []).check(
                                Perms.MASTER
                            )
                        }
                    >
                        <button
                            class=' icon'
                            classList={{ red: hasPerms(), blue: !hasPerms() }}
                            onclick={e => {
                                e.stopPropagation()
                                let title = hasPerms() ? 'میگیرید' : 'میدهید'
                                let type = hasPerms() ? 'error' : 'info'
                                let Icon = hasPerms()
                                    ? ShieldDisableIcon
                                    : ShieldCheckIcon

                                setPopup({
                                    show: true,
                                    title: `دسترسی ادمین را ${title}؟`,
                                    timer: 1,
                                    type: type as any,
                                    Icon,
                                    onSubmit: toggleAdmin,
                                })
                            }}
                        >
                            <Show
                                when={hasPerms()}
                                fallback={<ShieldCheckIcon />}
                            >
                                <ShieldDisableIcon />
                            </Show>
                        </button>
                    </Show>
                </div>
            </td>
        </tr>
    )
}

export { type STATE as USERSTATE }

export default Users
